import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, Clock, Plug, RefreshCw, Unplug, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, Skeleton } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { importSpreadsheetSearch } from '@/lib/import-search';
import { api } from '@/lib/utils';
import { BRAND } from '@maintenance-tracker/shared';

interface IntegrationItem {
  provider: string;
  name: string;
  description: string;
  reads: string[];
  writes: string[];
  contactUs?: boolean;
  status: 'connected' | 'disconnected' | 'error' | 'pending';
  connectedAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  externalAccountId: string | null;
  recordsSynced: number;
}

interface IntegrationsResponse {
  integrations: IntegrationItem[];
  integrations_live: boolean;
  plan_allows_integrations: boolean;
}

export function IntegrationsPage() {
  const qc = useQueryClient();
  const search = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const connectedProvider = search.get('connected');
  const errorMsg = search.get('error');
  const noticeMsg = search.get('notice');

  const { data, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api<IntegrationsResponse>('/api/v1/integrations'),
  });

  const integrationsLive = data?.integrations_live ?? false;

  useEffect(() => {
    if (!integrationsLive && (connectedProvider || errorMsg || noticeMsg)) {
      window.history.replaceState({}, '', '/settings/integrations');
    }
  }, [integrationsLive, connectedProvider, errorMsg, noticeMsg]);

  const syncMutation = useMutation({
    mutationFn: (provider: string) =>
      api(`/api/v1/integrations/${provider}/sync`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  });

  const disconnectMutation = useMutation({
    mutationFn: (provider: string) =>
      api(`/api/v1/integrations/${provider}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Integrations"
        description="Import your book from a spreadsheet today. Live connections to QuickBooks, Jobber, and Housecall Pro are rolling out soon."
      />

      <Card
        title="Spreadsheet import"
        description="Available on every plan — the fastest way to get started. Import agreements, customers, and service history from Excel or a CSV export."
      >
        <p className="text-sm text-text-secondary">
          Exports from Excel, Jobber, QuickBooks, or your FSM usually work. Map columns once and we score renewal risk automatically.
        </p>
        <Link to="/retention" search={importSpreadsheetSearch('agreements') as never} className="mt-4 inline-block">
          <Button>
            <Upload className="h-4 w-4" /> Import from spreadsheet
          </Button>
        </Link>
      </Card>

      {!integrationsLive && !isLoading && (
        <div className="rounded-lg border border-border bg-bg-subtle px-4 py-3 text-sm text-text-secondary">
          <p className="font-medium text-text-primary">App integrations — coming soon</p>
          <p className="mt-1">
            We&apos;re finishing OAuth setup with QuickBooks, Jobber, and Housecall Pro. You&apos;ll be able to connect
            and sync automatically on Pro when this launches — use spreadsheet import in the meantime.
          </p>
        </div>
      )}

      {integrationsLive && connectedProvider && (
        <div className="rounded-lg bg-success-subtle px-4 py-3 text-sm text-[#166534]">
          Connected {connectedProvider.replace(/_/g, ' ')} successfully.
        </div>
      )}
      {integrationsLive && errorMsg && (
        <div className="rounded-lg bg-danger-subtle px-4 py-3 text-sm text-[#991B1B]">
          Integration error ({errorMsg.replace(/_/g, ' ')}).
        </div>
      )}

      {integrationsLive && !data?.plan_allows_integrations && !isLoading && (
        <Card title="Upgrade required" description="Live integrations are included on Pro.">
          <p className="text-sm text-text-secondary">
            Spreadsheet import is available on every plan from the At risk dashboard. Upgrade to sync QuickBooks, Jobber, or Housecall Pro automatically.
          </p>
          <Link to="/settings/billing" className="mt-4 inline-block">
            <Button>View plans</Button>
          </Link>
        </Card>
      )}

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-text-primary">Field service &amp; accounting apps</h2>
        <p className="text-sm text-text-secondary">
          {integrationsLive
            ? 'Connect the apps your shop already runs. We read customers, agreements, and billing signals — we never write to your FSM unless you opt in.'
            : 'Planned for Pro — connect and sync when live integrations launch.'}
        </p>
        <div className="grid gap-4">
          {isLoading
            ? [1, 2, 3].map((n) => <Skeleton key={n} className="h-44 rounded-xl" />)
            : data?.integrations.map((item) => (
                <Card key={item.provider} title={item.name} description={item.description}>
                  <div className="space-y-4">
                    {!integrationsLive && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-bg-muted px-2.5 py-1 text-xs font-medium text-text-muted">
                        <Clock className="h-3.5 w-3.5" /> Coming soon
                      </span>
                    )}
                    <div className="flex flex-wrap gap-2 text-xs text-text-muted">
                      <span className="rounded-full bg-bg-muted px-2 py-0.5">Reads: {item.reads.join(', ')}</span>
                      {item.writes.length > 0 && (
                        <span className="rounded-full bg-bg-muted px-2 py-0.5">Writes: {item.writes.join(', ')}</span>
                      )}
                    </div>

                    {integrationsLive && (item.status === 'connected' || item.status === 'error') ? (
                      <div className="space-y-2 text-sm">
                        <div className={`flex items-center gap-2 ${item.status === 'error' ? 'text-warning' : 'text-success'}`}>
                          {item.status === 'connected' ? <Check className="h-4 w-4" /> : <Plug className="h-4 w-4" />}
                          {item.status === 'connected' ? 'Connected' : 'Connected with sync errors'}
                        </div>
                        {item.lastSyncAt && (
                          <p className="text-text-muted">Last sync: {new Date(item.lastSyncAt).toLocaleString()}</p>
                        )}
                        {item.recordsSynced > 0 && (
                          <p className="text-text-muted">{item.recordsSynced} records synced to date</p>
                        )}
                        {item.lastError && <p className="text-[13px] text-danger">{item.lastError}</p>}
                        <div className="flex flex-wrap gap-2 pt-1">
                          <Button
                            variant="secondary"
                            loading={syncMutation.isPending && syncMutation.variables === item.provider}
                            onClick={() => syncMutation.mutate(item.provider)}
                          >
                            <RefreshCw className="h-4 w-4" /> Sync now
                          </Button>
                          <Button
                            variant="secondary"
                            loading={disconnectMutation.isPending && disconnectMutation.variables === item.provider}
                            onClick={() => disconnectMutation.mutate(item.provider)}
                          >
                            <Unplug className="h-4 w-4" /> Disconnect
                          </Button>
                        </div>
                      </div>
                    ) : integrationsLive && item.contactUs ? (
                      <p className="text-sm text-text-secondary">
                        ServiceTitan requires partner approval.{' '}
                        <a href={`mailto:${BRAND.helloEmail}`} className="font-medium text-brand hover:underline">
                          Contact us
                        </a>{' '}
                        to enable.
                      </p>
                    ) : integrationsLive ? (
                      <Button
                        onClick={async () => {
                          const res = await api<{ url: string }>(`/api/v1/integrations/${item.provider}/connect`);
                          if (res.url) window.location.href = res.url;
                        }}
                        disabled={!data?.plan_allows_integrations}
                      >
                        Connect
                      </Button>
                    ) : (
                      <p className="text-sm text-text-muted">
                        Not available yet — import a spreadsheet above, or email{' '}
                        <a href={`mailto:${BRAND.helloEmail}`} className="font-medium text-brand hover:underline">
                          {BRAND.helloEmail}
                        </a>{' '}
                        if you want early access.
                      </p>
                    )}
                  </div>
                </Card>
              ))}
        </div>
      </div>
    </div>
  );
}
